import moment from 'moment';

export const BALANCE_MODE = {
  CURRENT: 'current',
  AVAILABLE: 'available',
};

export function formatBalanceDate(date) {
  if (!date) {
    return '';
  }
  if (typeof date === 'string') {
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }
  const year = date.year;
  const month = date.month;
  const day = date.day;
  if (!year || !month || !day) {
    return '';
  }
  const parsed = moment([year, month - 1, day]);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

export function formatBalanceThousands(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return '';
  }
  const amount = Number(value);
  const thousands = Math.round(amount / 1000);
  const formatted = Math.abs(thousands).toLocaleString('en-US');
  const label = `$${formatted}K`;
  return amount < 0 ? `-${label}` : label;
}

export function pickBalanceValue(point, mode) {
  if (!point) {
    return null;
  }
  if (mode === BALANCE_MODE.AVAILABLE) {
    const available = point.available_balance ?? point.availableBalance;
    return available == null ? null : Number(available);
  }
  const current = point.current_balance ?? point.currentBalance;
  return current == null ? null : Number(current);
}

export function previousMonthSameDay(dateStr) {
  const parsed = moment(dateStr, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.subtract(1, 'month').format('YYYY-MM-DD');
}

export function fillDailyBalanceGaps(points) {
  if (!points || points.length === 0) {
    return [];
  }
  const sorted = [...points].sort((a, b) => a.date.localeCompare(b.date));
  const byDate = new Map(sorted.map((point) => [point.date, point]));
  const start = moment(sorted[0].date, 'YYYY-MM-DD', true);
  const end = moment(sorted[sorted.length - 1].date, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid()) {
    return sorted;
  }

  const filled = [];
  let lastPoint = sorted[0];
  for (
    let cursor = start.clone();
    cursor.isSameOrBefore(end, 'day');
    cursor.add(1, 'day')
  ) {
    const date = cursor.format('YYYY-MM-DD');
    const existing = byDate.get(date);
    if (existing) {
      lastPoint = existing;
      filled.push(existing);
    } else {
      filled.push({
        ...lastPoint,
        date,
      });
    }
  }
  return filled;
}

export function getHighlightDates(points) {
  if (!points || points.length === 0) {
    return { latest: '', previousMonth: '' };
  }
  const latest = points[points.length - 1].date;
  const previousMonth = previousMonthSameDay(latest);
  return { latest, previousMonth };
}

export function groupDailyBalancesByAccount(rows, selectedAccountIds = []) {
  const selected = new Set(selectedAccountIds.filter(Boolean));
  const byAccount = new Map();

  (rows || []).forEach((row) => {
    const accountId = row.account_id || row.accountId;
    if (!accountId) {
      return;
    }
    if (selected.size > 0 && !selected.has(accountId)) {
      return;
    }
    const date = formatBalanceDate(row.date);
    if (!date) {
      return;
    }

    if (!byAccount.has(accountId)) {
      byAccount.set(accountId, {
        accountId,
        bankName: row.bank_name || row.bankName || '',
        accountName: row.account_name || row.accountName || '',
        lastFour: row.last_four || row.lastFour || '',
        // Carried through so the chart title can tell one currency's balance
        // series from another's on the same account number.
        currencyCode: row.currency_code || row.currencyCode || '',
        points: [],
      });
    }

    byAccount.get(accountId).points.push({
      date,
      current_balance: Number(row.current_balance ?? row.currentBalance ?? 0),
      available_balance: Number(
        row.available_balance ?? row.availableBalance ?? 0
      ),
    });
  });

  return Array.from(byAccount.values()).map((account) => ({
    ...account,
    points: fillDailyBalanceGaps(account.points),
  }));
}
